import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import VerificationLabel from "@/components/VerificationLabel";
import { api } from "@/lib/api";
import { AlertTriangle, ArrowRight, ExternalLink } from "lucide-react";

export function MajorIndex() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/majors", { params: { page_size: 200 } })
      .then((r) => setItems(r.data.items || []))
      .finally(() => setLoading(false));
  }, []);

  return (
    <Layout>
      <SEO
        title="Florida Transfer Majors — Requirements & Pathways | Beyond Valencia"
        description="A directory of the most-transferred majors in Florida — with prerequisites, GPA thresholds, and limited-access flags."
        canonical="/majors"
      />
      <section className="bg-bone border-b border-stone/30">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 lg:py-24">
          <div className="text-[11px] uppercase tracking-[0.24em] text-brass font-medium">The Directory</div>
          <h1 className="mt-5 font-serif font-medium text-4xl sm:text-5xl lg:text-6xl text-ivy tracking-tight leading-[1.05]">
            Florida Transfer Majors
          </h1>
          <p className="mt-6 max-w-3xl text-base sm:text-lg text-ink-soft leading-relaxed">
            The 15 majors that account for most Florida community-college transfers. Each entry flags whether it&rsquo;s a limited-access program, what GPA threshold to plan for, and where the Common Prerequisites Manual is the source of truth.
          </p>
        </div>
      </section>
      <section className="bg-bone-dim/30">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-12 lg:py-16">
          {loading ? <p className="text-sm text-stone">Loading…</p> : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="majors-index-grid">
              {items.map((m) => (
                <Link key={m.slug} to={`/majors/${m.slug}`} className="border border-stone/40 bg-bone p-6 hover:border-ivy transition-colors group block" data-testid={`major-card-${m.slug}`}>
                  <div className="flex items-baseline justify-between mb-3">
                    <span className="text-[10px] uppercase tracking-[0.22em] text-brass font-semibold">
                      {m.limited_access ? "Limited-access" : "General admission"}
                    </span>
                    <VerificationLabel status={m.verification_status || "awaiting"} />
                  </div>
                  <h3 className="font-serif text-2xl text-ivy tracking-tight group-hover:text-brass transition-colors">{m.name}</h3>
                  {m.gpa_considerations && (
                    <p className="mt-4 text-sm text-ink-soft leading-relaxed line-clamp-3">{m.gpa_considerations}</p>
                  )}
                  <div className="mt-5 flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] font-semibold text-ivy group-hover:gap-2.5 transition-all">
                    Open major <ArrowRight className="h-3 w-3" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}

export function MajorDetail() {
  const { slug } = useParams();
  const [m, setM] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  useEffect(() => {
    api.get(`/majors/${slug}`).then((r) => setM(r.data)).catch(() => setError(true)).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <Layout><div className="max-w-4xl mx-auto p-16 text-stone">Loading…</div></Layout>;
  if (error || !m) return <Layout><div className="max-w-4xl mx-auto p-16"><h1 className="font-serif text-4xl text-ivy">Not found</h1></div></Layout>;

  const isPlaceholder = m._placeholder || m.verification_status !== "verified";

  return (
    <Layout>
      <SEO
        title={`${m.name} Transfer Requirements — Florida Colleges & Universities | Beyond Valencia`}
        description={isPlaceholder ? `${m.name} — Florida transfer major profile coming soon.` : (m.body_markdown || "").slice(0, 200)}
        canonical={`/majors/${slug}`}
        noindex={isPlaceholder}
        jsonLd={!isPlaceholder ? { "@context": "https://schema.org", "@type": "Course", "name": m.name, "provider": { "@type": "Organization", "name": "Florida State University System" } } : null}
      />

      <section className="bg-ivy text-bone border-b border-ivy-light" data-testid="major-detail-hero">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-16 lg:py-20">
          <div className="flex items-baseline gap-3 mb-4">
            <span className="text-[10px] uppercase tracking-[0.24em] text-brass-soft font-medium">
              {m.limited_access ? "Limited-Access Major" : "General Admission Major"}
            </span>
            <VerificationLabel status={m.verification_status || "awaiting"} />
          </div>
          <h1 className="font-serif font-medium text-4xl sm:text-5xl lg:text-[3.5rem] tracking-tight leading-[1.05]">
            {m.name}
          </h1>
          {m.gpa_considerations && (
            <p className="mt-6 max-w-2xl text-base sm:text-lg text-bone-dim leading-relaxed">{m.gpa_considerations}</p>
          )}
        </div>
      </section>

      <section className="bg-bone">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-16 lg:py-20">
          {isPlaceholder ? (
            <div className="border border-brass/40 bg-brass/5 p-8 lg:p-10" data-testid="major-placeholder">
              <div className="text-[11px] uppercase tracking-[0.24em] text-brass font-semibold">Profile Coming Soon</div>
              <h2 className="mt-4 font-serif font-medium text-3xl text-ivy tracking-tight leading-tight">
                This major profile is being verified by our transfer research team.
              </h2>
              <p className="mt-5 text-base text-ink-soft leading-relaxed">
                Meanwhile, ask NEO about {m.name} at Florida universities, or book a Transfer Audit to get a major-fit assessment.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <Link to="/neo" className="inline-flex items-center gap-2 bg-brass hover:bg-brass-soft text-ink uppercase tracking-[0.18em] text-[11px] font-semibold py-3 px-5 transition-colors">
                  Ask NEO about {m.name} <ArrowRight className="h-3.5 w-3.5" />
                </Link>
                <Link to="/transfer-audit" className="inline-flex items-center gap-2 border border-ivy text-ivy hover:bg-ivy hover:text-bone uppercase tracking-[0.18em] text-[11px] font-semibold py-3 px-5 transition-colors">
                  Book a Transfer Audit
                </Link>
              </div>
              {m.official_source_url && (
                <p className="mt-6 text-xs text-stone">
                  Official source: <a href={m.official_source_url} target="_blank" rel="noopener noreferrer" className="text-brass border-b border-brass/50">{m.official_source_url}</a>
                </p>
              )}
            </div>
          ) : (
            <div className="text-[16px] text-ink leading-relaxed whitespace-pre-line">{m.body_markdown}</div>
          )}

          {m.limited_access && (
            <div className="mt-12 border-l-4 border-crimson bg-crimson/5 px-6 py-5">
              <div className="flex items-baseline gap-2 mb-3">
                <AlertTriangle className="h-4 w-4 text-crimson flex-shrink-0" />
                <div className="text-[11px] uppercase tracking-[0.24em] font-semibold text-crimson">Limited-access — read this</div>
              </div>
              <p className="text-sm text-ink-soft leading-relaxed">
                {m.name} is a limited-access major at most Florida universities. This means the AA guarantee does NOT extend to admission into this major. A separate departmental application with major-specific GPA thresholds and prerequisite courses is required.
              </p>
            </div>
          )}

          {m.common_prerequisites?.length > 0 && (
            <div className="mt-12">
              <div className="text-[10px] uppercase tracking-[0.24em] text-brass font-semibold mb-3">Common prerequisites</div>
              <ul className="space-y-2 text-sm text-ink-soft">
                {m.common_prerequisites.map((p, i) => <li key={i}>• {p}</li>)}
              </ul>
            </div>
          )}

          {m.offered_at_details?.length > 0 && (
            <div className="mt-12">
              <div className="text-[10px] uppercase tracking-[0.24em] text-brass font-semibold mb-3">Offered at</div>
              <div className="flex flex-wrap gap-2">
                {m.offered_at_details.map((s) => (
                  <Link key={s.slug} to={`${s.type === "community_college" ? "/community-colleges" : "/universities"}/${s.slug}`} className="border border-stone/40 bg-bone-dim/40 px-3 py-1.5 text-xs text-ink hover:border-ivy transition-colors">
                    {s.name}
                  </Link>
                ))}
              </div>
            </div>
          )}

          {m.official_source_url && (
            <p className="mt-12 pt-6 border-t border-stone/30 text-xs text-stone">
              <ExternalLink className="h-3 w-3 inline mr-1" /> Source: <a href={m.official_source_url} target="_blank" rel="noopener noreferrer" className="text-brass border-b border-brass/40">{m.official_source_url}</a>
            </p>
          )}
        </div>
      </section>
    </Layout>
  );
}
